import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import {
    getPageLinkByNodeId,
    LinkModel,
    useGetLink,
    useGetLinks,
} from "@insite/client-framework/Store/Links/LinksSelectors";
import { HasFields } from "@insite/client-framework/Types/ContentItemModel";
import { LinkFieldValue } from "@insite/client-framework/Types/FieldDefinition";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import Icon, { IconPresentationProps } from "@insite/mobius/Icon";
import Link from "@insite/mobius/Link";
import Typography, { TypographyProps } from "@insite/mobius/Typography";
import breakpointMediaQueries from "@insite/mobius/utilities/breakpointMediaQueries";
import getColor from "@insite/mobius/utilities/getColor";
import getProp from "@insite/mobius/utilities/getProp";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { TYPOGRAPHY } from "@tCentro/theme/design";

import React, { FC } from "react";
import { css } from "styled-components";

const enum fields {
    videoURL = "videoURL",
    mainTitle = "mainTitle",
    title = "title",
    titleLink = "titleLink",
    links = "links",
    cardLinkURL = "cardLinkURL",
}

interface LinkItemFields {
    destination: { type: string; value: string };
    overriddenTitle: string;
    openInNewWindow: boolean;
    thumbnail?: string;
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.title]: string;
        [fields.videoURL]: string;
        [fields.titleLink]: string;
        [fields.mainTitle]: string;
        [fields.links]: LinkModel[];
        [fields.cardLinkURL]: LinkFieldValue;
    };
}

export interface CENBrandResourcesStyles {
    maincontainerStyles: GridContainerProps;
    mainTitleStyles: InjectableCss;
    subTitleStyles: InjectableCss;
    headerGridItemStyles: GridItemProps;
    subHeaderGridItemStyles: GridItemProps;
    linkGridItemStyles: GridItemProps;
    linkListStyles: InjectableCss;
    linkRowStyles: InjectableCss;
    thumbnailStyles: InjectableCss;
    linkTextStyles: InjectableCss;
}

const ROW_WHITE = "#ffffff";
const ROW_GRAY = "#f4f4f4";

export const cenBrandResourcesStyles: CENBrandResourcesStyles = {
    maincontainerStyles: {
        gap: 0,
        css: css`
            width: 100%;
            margin: 0 20px;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        margin: 40px 0 0;
                    `,
                    css`
                        margin: 40px 0 0;
                    `,
                    css`
                        margin: 40px 0 0;
                    `,
                    null,
                    null,
                ])}
        `,
    },
    mainTitleStyles: {
        css: css`
            font-size: ${TYPOGRAPHY.size.xxlarge};
            font-weight: ${TYPOGRAPHY.weight.bold};
            line-height: 40px;
            color: ${getProp("theme.typography.p.color")};
            margin-bottom: 8px;
        `,
    },
    subTitleStyles: {
        css: css`
            font-size: ${TYPOGRAPHY.size.largest};
            font-weight: ${TYPOGRAPHY.weight.bold};
            line-height: 32px;
            color: ${getProp("theme.typography.p.color")};
            margin-bottom: 16px;
        `,
    },
    headerGridItemStyles: {
        width: [12, 12, 12, 12, 12],
        css: css`
            padding: 0px 0px 4px 0px;
        `,
    },
    subHeaderGridItemStyles: {
        width: [12, 12, 12, 12, 12],
        css: css`
            padding: 0px;
        `,
    },
    linkGridItemStyles: {
        width: [12, 12, 12, 12, 12],
        css: css`
            padding: 0;
        `,
    },
    linkListStyles: {
        css: css`
            display: grid;
            grid-template-columns: 1fr;
            gap: 0;
            width: 100%;

            & > * {
                background-color: ${ROW_WHITE};
            }
            & > *:nth-child(even) {
                background-color: ${ROW_GRAY};
            }

            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    null,
                    null,
                    css`
                        grid-template-columns: 1fr 1fr;
                        column-gap: 24px;

                        & > * {
                            background-color: ${ROW_WHITE};
                        }
                        & > *:nth-child(even) {
                            background-color: ${ROW_WHITE};
                        }
                        & > *:nth-child(4n + 3),
                        & > *:nth-child(4n) {
                            background-color: ${ROW_GRAY};
                        }
                    `,
                    css`
                        grid-template-columns: 1fr 1fr;
                        column-gap: 24px;

                        & > * {
                            background-color: ${ROW_WHITE};
                        }
                        & > *:nth-child(even) {
                            background-color: ${ROW_WHITE};
                        }
                        & > *:nth-child(4n + 3),
                        & > *:nth-child(4n) {
                            background-color: ${ROW_GRAY};
                        }
                    `,
                    css`
                        grid-template-columns: 1fr 1fr;
                        column-gap: 24px;

                        & > * {
                            background-color: ${ROW_WHITE};
                        }
                        & > *:nth-child(even) {
                            background-color: ${ROW_WHITE};
                        }
                        & > *:nth-child(4n + 3),
                        & > *:nth-child(4n) {
                            background-color: ${ROW_GRAY};
                        }
                    `,
                ])}
        `,
    },
    linkRowStyles: {
        css: css`
            display: flex;
            align-items: center;
            padding: 16px 24px;
            height: 100%;
            box-sizing: border-box;
            gap:24px;
            max-height:94px;
        `,
    },
    thumbnailStyles: {
        css: css`
            width: 53px;
            height: 64px;
            min-width: 53px;
            margin-right: 16px;
            object-fit: contain;
        `,
    },
    linkTextStyles: {
        css: css`
            font-size: ${TYPOGRAPHY.size.base};
            font-weight: ${TYPOGRAPHY.weight.mediumbold};
            line-height: 18px;
        `,
    },
};

const styles = cenBrandResourcesStyles;

// Generic fallback thumbnail used when a link has no thumbnail image configured.
const DefaultThumbnail = () => (
    <svg viewBox="0 0 36 36" width="36" height="36" xmlns="http://www.w3.org/2000/svg">
        <rect x="6" y="2" width="24" height="30" rx="2" fill="#ffffff" stroke="#bfbfbf" strokeWidth="1.5" />
        <line x1="10" y1="9" x2="26" y2="9" stroke="#bfbfbf" strokeWidth="1.2" />
        <line x1="10" y1="13" x2="26" y2="13" stroke="#bfbfbf" strokeWidth="1.2" />
        <line x1="10" y1="17" x2="22" y2="17" stroke="#bfbfbf" strokeWidth="1.2" />
        <rect x="6" y="27" width="24" height="5" fill="#0f96d9" />
    </svg>
);

const CENBrandResourcesGuide: FC<OwnProps> = ({ fields }) => {
    const { links } = useGetLinks(fields.links, o => o.fields.destination);

    return (
        <GridContainer {...styles.maincontainerStyles}>
            {/* <GridItem {...styles.headerGridItemStyles}>
                <Typography {...styles.mainTitleStyles}>{fields.mainTitle}</Typography>
            </GridItem> */}

            {fields.title && (
                <GridItem {...styles.subHeaderGridItemStyles}>
                    <Typography {...styles.subTitleStyles}>{fields.title}</Typography>
                </GridItem>
            )}

            <GridItem {...styles.linkGridItemStyles}>
                <StyledWrapper css={styles.linkListStyles.css}>
                    {links.map((link, index) => {
                        if (!link.url) {
                            return null;
                        }

                        const linkFields = fields.links[index].fields as LinkItemFields;

                        return (
                            <StyledWrapper key={index} {...styles.linkRowStyles}>
                                {linkFields.thumbnail ? (
                                    <img src={linkFields.thumbnail} alt="" {...styles.thumbnailStyles} />
                                ) : (
                                    <div{...styles.thumbnailStyles}>
                                        <img src="/UserFiles/Images/brandresourceImg.png" alt="" {...styles.thumbnailStyles} />
                                    </div>
                                )}
                                <Link
                                    href={link.url}
                                    target={linkFields.openInNewWindow ? "_blank" : ""}
                                    {...styles.linkTextStyles}
                                >
                                    {linkFields.overriddenTitle || link.title}
                                </Link>
                            </StyledWrapper>
                        );
                    })}
                </StyledWrapper>
            </GridItem>
        </GridContainer>
    );
};

const widgetModule: WidgetModule = {
    component: CENBrandResourcesGuide,
    definition: {
        group: "Basic",
        icon: "ax-link-list",
        displayName: "CEN Brand Resources Guide",
        fieldDefinitions: [
           
            {
                name: fields.title,
                editorTemplate: "TextField",
                displayName: "Section Subtitle",
                defaultValue: "Guides",
                fieldType: "General",
            },
           
            {
                name: fields.links,
                editorTemplate: "ListField",
                getDisplay: (item: HasFields, state) => {
                    const {
                        destination: { type, value },
                        overriddenTitle,
                    } = item.fields;
                    if (overriddenTitle) {
                        return overriddenTitle;
                    }
                    if (type === "Page") {
                        const link = getPageLinkByNodeId(state, value);
                        return link?.title;
                    }
                    if (type === "Category") {
                        let displayValue = "";
                        const categories = (state as any).pageEditor.categories;
                        if (!categories) {
                            displayValue = "Loading...";
                        } else {
                            const matchingCategories = categories.filter((o: { id: string }) => o.id === value);
                            if (matchingCategories.length === 0) {
                                displayValue = "Unknown";
                            } else {
                                displayValue = matchingCategories[0].shortDescription;
                            }
                        }

                        return displayValue;
                    }
                    if (type === "Url") {
                        return value;
                    }
                    return value;
                },
                defaultValue: [],
                fieldType: "General",
                fieldDefinitions: [
                    {
                        name: "destination",
                        editorTemplate: "LinkField",
                        defaultValue: {
                            value: "",
                            type: "Page",
                        },
                        isRequired: true,
                    },
                    {
                        name: "overriddenTitle",
                        displayName: "Link Title",
                        editorTemplate: "TextField",
                        defaultValue: "",
                    },
                    {
                        name: "thumbnail",
                        displayName: "Thumbnail Image",
                        editorTemplate: "ImagePickerField",
                        defaultValue: "",
                    },
                    {
                        name: "openInNewWindow",
                        editorTemplate: "CheckboxField",
                        defaultValue: false,
                    },
                ],
            },
          
        ],
    },
};

export default widgetModule;